import { InsuranceProduct } from '@front/apis'
import { usePetInsurance } from '@front/logic'
import { AppBackground, BottomActionContainer, Button, Text, ResponsiveLogo } from '@ui/components'
import React, { useEffect } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { setLoading, useAppSelector } from '@front/store'
import { ReactComponent as AenaSeguroIcon } from '@/assets/icons/aena/aena_seguro.svg'
// import { Banner, MainContainer, PolicyInfoContainer, PolicyInfoContent, PolicyButtonContainer, SadPetImage } from "@/aena/styles"
import { View } from "react-native"
import { t } from 'i18next' 
import { HeaderWithBackAena } from '@/components/headerwithback'
import { PETPASS_ExternalInsuranceType, PETPASS_insurance_recurrence } from '@/db/schema/types'
import webStyled from '@emotion/styled'
import { Banner } from '@/aena/styles'
import styled from '@emotion/native'

const SadPetImage = webStyled.img(() => ({
  width: 200,
  height: 200,
  alignSelf: 'center',
  resizeMode: 'cover',
}))

const PolicyInfoContainer = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.sizes.sm,
  paddingVertical: theme.sizes.xs,
}))

export interface ActualPolicyInfo {
  guid: string
  title: string
  subtitle: string
  price?: string
  color?: string
  policiInfo: { key: string; value: string }[]
  image: string
  isExternalInsurance: boolean
  products: InsuranceProduct[]
  fileRef?: string
  previewUrl?: string
  insurance: {
    guid: string
    policyNumber: string
    endAt: string
    fileRef?: string
    type: string
    recurrence: string
    status: string
    company: {
      id: string
      guid: string
      name: string
    }
  }
}

export interface ExternalInsuranceInfo {
  guid: string
  policyNumber: string
  endAt: string
  fileRef?: string
  type: string
  recurrence: string
  status: string
  company: {
    id: string
    guid: string
    name: string
  }
}

export interface NoInsuranceInfoProps {
  title: string
  description: string
  image: string
}

export const Insurance: React.FC = () => {
  const { getInsurance } = usePetInsurance()
  const { name } = useAppSelector((state) => state.selectedPet)
  const [hasInsurance, setHasInsurance] = React.useState<boolean | undefined>(undefined)
  const [insurance, setInsurance] = React.useState<ActualPolicyInfo | undefined>(undefined)
  const [noInsuranceInfo, setNoInsuranceInfo] = React.useState<NoInsuranceInfoProps | undefined>(undefined)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const PAYMENT_RECURRENCE = [
    { id: "MONTHLY", title: t('insurance.payment.monthly'), data: PETPASS_insurance_recurrence.MONTHLY },
    { id: "QUARTERLY", title: t('insurance.payment.quarterly'), data: PETPASS_insurance_recurrence.QUARTERLY },
    { id: "ANNUAL", title: t('insurance.payment.annual'), data: PETPASS_insurance_recurrence.ANNUAL },
  ]
  const INSURANCE_TYPES = [
    { id: "CIVIL_LIABILITY", title: t('insurance.types.liability'), data: PETPASS_ExternalInsuranceType.CIVIL_LIABILITY },
    { id: "VETERINARY_LIABILITY", title: t('insurance.types.veterinary_liability'), data: PETPASS_ExternalInsuranceType.VETERINARY_LIABILITY },
    { id: "HOME_LIABILITY", title: t('insurance.types.home_liability'), data: PETPASS_ExternalInsuranceType.HOME_LIABILITY },
    { id: "OTHER", title: t('insurance.types.other'), data: PETPASS_ExternalInsuranceType.OTHER },
  ]
  

  useEffect(() => {
    const getInsuranceDetails = async () => {
      dispatch(setLoading(true))
      const insurance = await getInsurance()
      setHasInsurance(insurance?.hasInsurance)
      if (!insurance) {
        dispatch(setLoading(false))
        return
      }
      console.log('insurance----', { insurance })
      insurance.hasInsurance ?
        setInsurance({
          guid: insurance.guid,
          color: insurance.color,
          price: insurance.price,
          subtitle: insurance?.description ?? '',
          policiInfo: [],
          insurance: insurance.insurance,
          title: insurance?.title ?? '',
          image: insurance?.image ?? '',
          isExternalInsurance: insurance?.isExternalInsurance ?? false,
          products: insurance?.products ?? [{} as any],
        }) :
        setNoInsuranceInfo({
          description: insurance?.description ?? '',
          image: insurance?.image ?? '',
          title: insurance?.title ?? '',
        })
      dispatch(setLoading(false))
    }
    getInsuranceDetails()

  }, [])


  const onEditInsurancePress = async () => {
      console.log({ externalInsuranceGuid: insurance?.insurance?.guid })
      if (insurance?.insurance?.guid) { 
        navigate('editexternal', { state: { externalInsuranceGuid: insurance?.insurance?.guid, externalInsurance: insurance?.insurance } })
      }
  }

  return  (
  <AppBackground isSafe>
  <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
    <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
      <ResponsiveLogo
        breakpoint={820}
        hysteresis={20}
        large={{ width: 680, height: 96 }}
        small={{ width: 360, height: 48 }}
      />
    </View>
    <HeaderWithBackAena title={(t('common.your_insurance'))} />
    <BottomActionContainer>
    {insurance ? 
      // Insurance
      <View style={{ gap: 16 }}>
        <View style={{ flex: 1, width: '100%', maxWidth: 400, alignSelf: 'center' }}>
          <Button
            title={t('insurance.external.view_policy') as string}
            size="block"
            color="aena"
            textColor="background0"
            textType="cta_medium"
            borderRadius={5}
            onPress={() => {
              const ref = insurance.insurance.fileRef
              const url = ref ? `https://aena.api.petpass.pro/storage/ref/${ref}` : ''
              if (url) window.open(url, '_blank')
            }}
          />
        </View>
      <View style={{ flexDirection: 'column', alignSelf: 'center', width: '100%', maxWidth: 400 }}>
        <PolicyInfoContainer>
          <Text type="H5" fontWeight="bold" fontFamily="bold">{t('insurance.policy_number')}</Text>
          <Text type="caption">{insurance.insurance.policyNumber}</Text>
        </PolicyInfoContainer>

        <PolicyInfoContainer>
          <Text type="H5" fontWeight="bold" fontFamily="bold">{t('insurance.insurancecompany')}</Text>
          <Text type="caption">{insurance.insurance.company.name}</Text>
        </PolicyInfoContainer>

        <PolicyInfoContainer>
          <Text type="H5" fontWeight="bold" fontFamily="bold">{t('insurance.expiration_date')}</Text>
          <Text type="caption">{insurance.insurance.endAt ? new Date(insurance.insurance.endAt).toLocaleDateString() : ''}</Text>
        </PolicyInfoContainer>

        <PolicyInfoContainer>
          <Text type="H5" fontWeight="bold" fontFamily="bold">{t('insurance.external.type_label')}</Text>
          <Text type="caption">{ insurance.insurance.type ? INSURANCE_TYPES.find((item) => item.id === insurance.insurance.type)?.title : ''}</Text>
        </PolicyInfoContainer>

          <PolicyInfoContainer>
            <Text type="H5" fontWeight="bold" fontFamily="bold">{t('insurance.external.payment_recurrence_label')}</Text>
            <Text type="caption">{insurance.insurance.recurrence ? PAYMENT_RECURRENCE.find((item) => item.id === insurance.insurance.recurrence)?.title : ''}</Text>
          </PolicyInfoContainer>
      </View>
        <Button
          title={(t('common.edit_insurance'))}
          size="block"
          textType="cta_medium"
          borderRadius={5}
          textColor="background0"
          color="aena"
          onPress={onEditInsurancePress}
          style={{ marginTop: 40 }}
        />
      </View>
    : 
      // No insurance
      <View>
        <View style={{ gap: 26, alignItems: 'center' }}>
          <AenaSeguroIcon width={200} height={180} />
          <Text type="H4" fontWeight="bold" fontFamily="bold" textAlign="center">{t('insurance.no_insurance.title',{name: name})}</Text>
          <Text type="caption" textAlign="center">{t('insurance.no_insurance.description')}</Text>
        </View>
        <Button
          title={(t('common.add_external_insurance'))}
          size="block"
          textType="cta_medium"
          borderRadius={5}
          textColor="background0"
          buttonWrapperStyle={{ backgroundColor: '#96ce00' }}
          color="aena"
          onPress={() => navigate(`addexternal`)}
          style={{ marginTop: 60 }}
        />
      </View>
    }
    </BottomActionContainer>
  </View>
  </AppBackground>
  )
  

  if (insurance) {
    return (
      <AppBackground isSafe>
        <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
          <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
            <ResponsiveLogo
              breakpoint={820}
              hysteresis={20}
              large={{ width: 680, height: 96 }}
              small={{ width: 360, height: 48 }}
            />
          </View>
          <HeaderWithBackAena title={(t('common.your_insurance'))} />
          <BottomActionContainer>
            <React.Fragment>
 



            </React.Fragment>
          </BottomActionContainer>
        </View>
      </AppBackground>
    )
  }
  // No insurance
  return (
      <AppBackground isSafe>
        <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
          <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
            <ResponsiveLogo
              breakpoint={820}
              hysteresis={20}
              large={{ width: 680, height: 96 }}
              small={{ width: 360, height: 48 }}
            />
          </View>
          <HeaderWithBackAena title={(t('common.your_insurance'))} />
          <BottomActionContainer>
            <View style={{ alignItems: 'center' }}>
              <AenaSeguroIcon width={200} height={180} />
              <Text type="H4" fontWeight="bold" fontFamily="bold" textAlign="center">{t('insurance.no_insurance.title',{name: name})}</Text>
              <Text type="caption" textAlign="center">{t('insurance.no_insurance.description')}</Text>
            </View>
            <Button
              title={(t('common.add_external_insurance'))}
              size="block"
              textType="cta_medium"
              borderRadius={5}
              textColor="background0"
              buttonWrapperStyle={{ backgroundColor: '#96ce00' }}
              color="aena"
              onPress={() => navigate(`addexternal`)}
            />
          </BottomActionContainer>
        </View>
      </AppBackground>
    )
};

export default Insurance;
